/**
 * Reset Password Page Component
 * 
 * Page for users to reset their password after clicking the reset link in their email.
 * 
 * Features:
 * - New password input field
 * - Confirm password input field
 * - Password strength validation
 * - Submit button
 * - Success message and redirect to login
 * - Error handling
 * - Extract token from URL hash (Supabase uses hash fragments)
 * - Use supabase.auth.updateUser() to set new password
 * - WCAG 2.2 AA compliance
 * - Dark mode support
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Eye, EyeOff, CheckCircle, ArrowRight, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';

export function ResetPasswordPage(): React.ReactElement {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState(false);
  const [hasRecoveryHash, setHasRecoveryHash] = useState(false);
  const [checkingToken, setCheckingToken] = useState(true);

  // Check if we have a valid recovery token in the URL hash
  useEffect(() => {
    let mounted = true;
    let subscription: { data: { subscription: { unsubscribe: () => void } } } | null = null;

    const checkToken = async () => {
      try {
        // Check if we have a hash fragment with recovery token
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const type = hashParams.get('type');

        console.log('Hash params:', { accessToken: !!accessToken, type });

        // If no hash fragment, check if we already have a session (user might have refreshed)
        if (!accessToken || type !== 'recovery') {
          // Check if we already have a valid session
          const { data: { session } } = await supabase.auth.getSession();
          console.log('No hash, checking existing session:', !!session?.user);
          if (session?.user && mounted) {
            // User already has a session, allow password reset
            setHasRecoveryHash(true);
            setCheckingToken(false);
            return;
          }
          
          if (mounted) {
            setHasRecoveryHash(false);
            setError('Invalid or missing password reset token. Please request a new password reset link.');
            setCheckingToken(false);
          }
          return;
        }

        // We have a recovery hash - mark it as valid and let Supabase process it
        setHasRecoveryHash(true);

        // Supabase automatically processes the hash when detectSessionInUrl is true
        // Listen for auth state changes to know when the session is set
        subscription = supabase.auth.onAuthStateChange(async (event, session) => {
          if (!mounted) return;

          console.log('Auth state change event:', event, 'Has session:', !!session?.user, 'User email:', session?.user?.email);

          if (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
            if (session?.user && mounted) {
              // Password recovery event or signed in with recovery token
              setCheckingToken(false);
              if (subscription) {
                subscription.data.subscription.unsubscribe();
              }
            }
          }
        });

        // Manually trigger session refresh to process the hash
        // Supabase should automatically process it, but we'll also check manually
        const checkSession = async (attempt: number = 1) => {
          if (!mounted) return;
          
          // Wait progressively longer on each attempt
          const delay = attempt * 500;
          await new Promise(resolve => setTimeout(resolve, delay));
          
          if (!mounted) return;
          
          // Get the current session
          const { data: { session }, error: sessionError } = await supabase.auth.getSession();
          
          console.log(`Session check attempt ${attempt} (after ${delay}ms):`, { 
            hasSession: !!session?.user, 
            userEmail: session?.user?.email,
            error: sessionError?.message,
            hash: window.location.hash.substring(0, 50) + '...'
          });

          // We have a hash, so allow the form to be shown
          // The actual session verification will happen on submit
          if (mounted) {
            setCheckingToken(false);
            if (subscription) {
              subscription.data.subscription.unsubscribe();
            }
          }
        };

        // Start checking
        checkSession(1);
      } catch (err: any) {
        console.error('Error checking token:', err);
        if (mounted) {
          setHasRecoveryHash(false);
          setError('Failed to verify reset token. Please request a new password reset link.');
          setCheckingToken(false);
        }
      }
    };

    checkToken();

    return () => {
      mounted = false;
      if (subscription) {
        subscription.data.subscription.unsubscribe();
      }
    };
  }, []);

  const validateForm = (): boolean => {
    setError('');

    if (!password) {
      setError('Password is required');
      return false;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return false;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Verify we have a valid session before updating password
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      console.log('Session check before password update:', { 
        hasSession: !!session, 
        hasUser: !!session?.user,
        userEmail: session?.user?.email,
        error: sessionError?.message 
      });

      if (sessionError || !session?.user) {
        setError('Your password reset session has expired. Please request a new password reset link.');
        setLoading(false);
        return;
      }

      // Update the user's password
      console.log('Attempting to update password...');
      const { data: updateData, error: updateError } = await supabase.auth.updateUser({
        password: password,
      });

      if (updateError) {
        console.error('Password update error:', updateError);
        setError(updateError.message || 'Failed to reset password. Please try again.');
        setLoading(false);
        return;
      }

      console.log('Password update successful:', { 
        userUpdated: !!updateData?.user,
        userEmail: updateData?.user?.email 
      });

      // Wait a moment to ensure the password update is fully processed
      await new Promise(resolve => setTimeout(resolve, 500));

      // Success - show success message and redirect to login
      setSuccess(true);
      
      // Sign out the recovery session
      // Note: We sign out to clear the recovery session, user will sign in with new password
      await supabase.auth.signOut();
      
      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/login', { 
          state: { 
            message: 'Password reset successfully! Please sign in with your new password.' 
          } 
        });
      }, 2000);
    } catch (err: any) {
      console.error('Password reset error:', err);
      setError(err.message || 'An unexpected error occurred. Please try again.');
      setLoading(false);
    }
  };

  // Show loading state while checking token
  if (checkingToken) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Verifying reset link...</p>
        </div>
      </div>
    );
  }

  // Show error if hash is invalid
  if (!checkingToken && !hasRecoveryHash) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4">
        <div className="relative w-full max-w-md">
          <div className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm rounded-3xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 p-10 ring-1 ring-gray-100 dark:ring-gray-800">
            <div className="text-center">
              <div className="flex justify-center mb-6">
                <div className="h-16 w-16 bg-error-100 dark:bg-error-900/30 rounded-full flex items-center justify-center">
                  <AlertCircle className="h-8 w-8 text-error-600 dark:text-error-400" />
                </div>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                Invalid Reset Link
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {error || 'This password reset link is invalid or has expired. Please request a new one.'}
              </p>
              <a
                href="/forgot-password"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 focus-ring"
              >
                <span>Request New Reset Link</span>
                <ArrowRight className="h-5 w-5" />
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4">
      {/* Background Pattern */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary-100 dark:bg-primary-900/20 rounded-full mix-blend-multiply dark:mix-blend-screen filter blur-xl opacity-70 animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-secondary-100 dark:bg-secondary-900/20 rounded-full mix-blend-multiply dark:mix-blend-screen filter blur-xl opacity-70 animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>

      <div className="relative w-full max-w-md">
        {/* Logo and Brand */}
        <div className="text-center mb-10">
          <div className="flex items-center justify-center gap-4 mb-6">
            <img 
              src="/logo.png"
              srcSet="/logo.png 1x, /Logo-L.png 2x, /Logo-XL.png 3x"
              alt="Money Mojo Logo" 
              className="h-12 w-12 object-contain"
              style={{ 
                imageRendering: 'auto',
                backfaceVisibility: 'hidden',
                transform: 'translateZ(0)'
              }}
              loading="eager"
              width="48"
              height="48"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
              }}
            />
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 font-brand tracking-tight">
              Money Mojo
            </h1>
          </div>
          <p className="text-lg text-gray-600 dark:text-gray-400 font-medium">
            Set your new password
          </p>
        </div>

        {/* Auth Card */}
        <div className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm rounded-3xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 p-10 ring-1 ring-gray-100 dark:ring-gray-800">
          {/* Success Message */}
          {success && (
            <div className="mb-6 p-4 rounded-lg bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-success-600 dark:text-success-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-success-800 dark:text-success-200 mb-1">
                    Password Reset Successful
                  </p>
                  <p className="text-sm text-success-700 dark:text-success-300">
                    Your password has been reset. Redirecting to sign in...
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 rounded-lg bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800">
              <p className="text-sm text-error-600 dark:text-error-400">
                {error}
              </p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-7">
            {/* New Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                New Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setError('');
                  }}
                  disabled={loading || success}
                  className="block w-full pl-12 pr-12 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="Enter your new password"
                  required
                  autoComplete="new-password"
                  minLength={8}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading || success}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Must be at least 8 characters
              </p>
            </div>

            {/* Confirm Password */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Confirm New Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setError('');
                  }}
                  disabled={loading || success}
                  className="block w-full pl-12 pr-12 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="Confirm your new password"
                  required
                  autoComplete="new-password"
                  minLength={8}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  disabled={loading || success}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || success}
              className="w-full flex items-center justify-center gap-2 py-3 px-6 bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed focus-ring"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Resetting Password...</span>
                </>
              ) : success ? (
                <>
                  <CheckCircle className="h-5 w-5" />
                  <span>Password Reset</span>
                </>
              ) : (
                <>
                  <span>Reset Password</span>
                  <ArrowRight className="h-5 w-5" />
                </>
              )}
            </button>
          </form>
        </div>

        {/* Footer */}
        <div className="mt-10 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Remember your password?{' '}
            <a 
              href="/login" 
              className="hover:underline text-primary-600 dark:text-primary-400 font-medium"
            >
              Sign In
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

